import { FC, useState } from 'react'
import { EmployeeSidebar, DetailsTab } from '@features/admin'
import { useTranslation } from 'react-i18next'
import { Tabs } from 'components/buttons'
import { HistoryTab } from './components/history-tab'
import { StatusBadge } from 'components/indicators/status-badge'
import { SidebarError } from './components/SidebarError'
import { useDateTimeToLocaleString } from '@hooks/useDateTimeWithLocale'
import { DateTime } from 'luxon'
import { faCalendarPen } from '@fortawesome/pro-solid-svg-icons'
import { Label } from 'components/indicators/label'
import { Status, WorkdaySendStatus } from 'types'
import { WorkdaySendError } from './components/WorkdaySendError'
import { TimeLoggingNoteTab } from '@features/admin/features/employee-sidebar/components/time-logging-note-tab/TimeLoggingNoteTab'
import { TimeBlocksTab } from '@features/admin/features/employee-sidebar/components/time-blocks-tab/TimeBlocksTab'
import { TaskOrderTab } from '@features/admin/features/employee-sidebar/components/task-order-tab'

type TabIds =
  | 'details'
  | 'history'
  | 'time-logging-note'
  | 'time-blocks'
  | 'task-order'

interface Props {
  date: DateTime
  employeeDetails: {
    department?: string | null
    email?: string
    fullName: string
    type: string
    phoneNumber?: string
    profileImageUrl?: string
    title?: string
  }
  employeeId: number
  holidayCalendarId?: string
  onClose: () => void
  timeCard?: AdminTimeCard // Not every day has a Time Card
  userId: number
}

export const Sidebar: FC<Props> = ({
  date,
  employeeDetails,
  holidayCalendarId,
  onClose,
  timeCard,
  employeeId,
  userId,
}) => {
  const { t } = useTranslation()
  const toLocaleString = useDateTimeToLocaleString()
  const [activeTab, setActiveTab] = useState<TabIds>('details')

  const tabs = [
    {
      id: 'details' as const,
      label: t('common.details'),
    },
    {
      id: 'history' as const,
      label: t('features.admin.history'),
    },
    {
      id: 'time-logging-note' as const,
      label: t('features.timeLogging.note'),
    },
    {
      id: 'time-blocks' as const,
      label: t('features.timeLogging.timeBlocks'),
    },
    {
      id: 'task-order' as const,
      label: t('features.timeLogging.taskOrder'),
    },
  ]

  const info = []

  if (timeCard) {
    info.push({
      [t('common.status')]: timeCard.correction ? (
        // TODO: Consider considering Corrected to be a first-class status
        <Label color="orange" icon={faCalendarPen} className="inline-flex">
          {t('features.timeLogging.corrected')}
        </Label>
      ) : (
        <StatusBadge status={timeCard.status} className="inline-flex" />
      ),
    })
  }

  const dateSection: { Date: string; 'Time Zone'?: string } = {
    Date: toLocaleString(DateTime.DATE_SHORT)(date), // Temporary until we have date picker
  }

  if (timeCard) {
    dateSection['Time Zone'] = timeCard.timeZone
  }

  info.push(dateSection)

  return (
    <EmployeeSidebar
      contentSlot={
        <div>
          {timeCard?.issues.map((issue) => (
            <SidebarError
              key={issue}
              title={t(`features.admin.timeCardIssueTypes.${issue}`)}
              content={''}
            />
          ))}
          {timeCard?.status === Status.Sent &&
            timeCard?.workdaySendStatus === WorkdaySendStatus.Error && (
              <WorkdaySendError timeCard={timeCard} />
            )}
        </div>
      }
      fullName={employeeDetails.fullName}
      department={employeeDetails.department}
      title={employeeDetails.title}
      type={employeeDetails.type}
      hidden={false}
      info={info}
      onClose={onClose}
      blockBreakpoint="3xl"
    >
      {<Tabs activeTab={activeTab} onTabClick={setActiveTab} tabs={tabs} />}
      <div className="overflow-auto px-8 py-4 flex flex-col">
        {activeTab === 'details' && (
          <DetailsTab
            date={date}
            timeCardId={timeCard?.id}
            userId={userId}
            holidayCalendarId={holidayCalendarId}
          />
        )}
        {activeTab === 'history' && (
          <HistoryTab date={date} employeeId={employeeId} />
        )}
        {activeTab === 'time-logging-note' && (
          <TimeLoggingNoteTab date={date} workdayWorkerId={employeeId} />
        )}
        {activeTab === 'time-blocks' && (
          <TimeBlocksTab
            date={date}
            timeCardId={timeCard?.id}
            timeZone={timeCard?.timeZone}
          />
        )}
        {activeTab === 'task-order' && (
          <TaskOrderTab date={date} timeCard={timeCard} />
        )}
      </div>
    </EmployeeSidebar>
  )
}
